import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useMedia } from 'react-use';
import { Button, CheckBox, Typography } from 'ck-ui';
import { isEmailValid } from 'ck-utils/validate';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input/mobile';
import { getStoreLocation } from 'ck-utils';
import { klaviyoSignUp } from './utils';
import styles from './FooterSignUpForm.module.scss';
import { usePhoneInputCountries } from './hooks';
import { TermsAndPrivacyLinks } from '../TermsAndPrivacyLinks';

const FooterSignUpForm = ({ nlText, onSignUpSuccess }) => {
  const isMobile = useMedia('(max-width: 767px)');
  const [isFetching, setIsFetching] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [preferences, setPreferences] = useState({
    men: false,
    women: false,
    kids: false,
  });
  const [smsConsent, setSMSConsent] = useState([]);
  const [smsConsentError, setSMSConsentError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const { defaultCountry, countries } = usePhoneInputCountries();
  const storeLocation = getStoreLocation();
  const validatePhone = () => {
    if (storeLocation === 'US' && !phone) return true;
    if (storeLocation === 'NZ' && smsConsent.length === 0 && !phone) return true;
    const isValid = phone ? isValidPhoneNumber(phone) : false;
    setPhoneError(isValid ? null : 'Please enter a valid mobile number.');
    return isValid;
  };

  const validateEmail = () => {
    const isValid = isEmailValid(email);
    setEmailError(isValid ? null : 'Please enter a valid email address.');
    return isValid;
  };

  const validateSmsConsent = () => {
    if (storeLocation !== 'NZ') return true;
    const isValid = !phone || (phone && smsConsent.length > 0);
    setSMSConsentError(isValid ? null : 'Please check at least one consent');
    return isValid;
  };

  const onChangeEmail = (e) => {
    setEmail(e.target.value);
  };

  const changePreferences = (e) => {
    setPreferences({ ...preferences, [e.target.name]: e.target.checked });
  };

  const changeSmsConsent = (e) => {
    const { name, checked } = e.target;
    if (checked && !smsConsent.includes(name)) {
      setSMSConsent([...smsConsent, name]);
    } else {
      setSMSConsent(smsConsent.filter(item => item !== name));
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validateEmail() || !validatePhone() || !validateSmsConsent()) return;
    setIsFetching(true);

    const signUpData = {
      email,
      phone,
      preferences,
      source: 'footer',
      onSuccess: onSignUpSuccess,
      onFinal: () => setIsFetching(false),
    };
    if (storeLocation === 'NZ') {
      signUpData.smsConsent = smsConsent;
    }
    klaviyoSignUp(signUpData);
  };

  return (
    <div className={styles.root}>
      <p className={classnames(styles.nlHeaderText, styles.nlMobileHeaderText)}>
        {nlText}
      </p>
      <div className={styles.textWrapper}>
        <p className={styles.nlHeaderText}>{nlText}</p>
        <form className={classnames('ck_email_signup', styles.nlForm)} onSubmit={onSubmit}>
          <Typography renderAs="legend" variant="heading8" weight={700} color="black-bis" style={{ margin: '0 auto' }}>Choose Your Product Preferences:</Typography>
          <label><CheckBox name="men" checked={preferences.men} onChange={changePreferences} className={styles.gutter}>Men</CheckBox></label>
          <label><CheckBox name="women" checked={preferences.women} onChange={changePreferences} className={styles.gutter}>Women</CheckBox></label>
          <label><CheckBox name="kids" checked={preferences.kids} onChange={changePreferences}>Kids</CheckBox></label>

          <div className={classnames('is-flex has-text-left', styles.phoneSmsWrapper)}>
            <div className={styles.box}>
              <label htmlFor="k_id_phone">
                <Typography variant="heading8" align="left" weight={700} color="black-bis">
                  Phone{storeLocation === 'US' || storeLocation === 'NZ' ? ' (Optional)' : '*'}
                </Typography>
              </label>
              <PhoneInput
                id="k_id_phone"
                defaultCountry={defaultCountry}
                countries={countries}
                countryCallingCodeEditable={false}
                countrySelectProps={{ unicodeFlags: true }}
                value={phone}
                className={classnames(styles.phoneInput, { [styles.error]: phoneError })}
                onChange={setPhone}
                onBlur={validatePhone}
              />
              {phoneError && (
                <Typography className="mt-1" variant="body-small" display="block" color="danger">{phoneError}</Typography>
              )}
            </div>

            <div className={styles.box}>
              <label htmlFor="k_id_email"><Typography variant="heading8" align="left" weight={700} color="black-bis">Email*</Typography></label>
              <div>
                <input
                  aria-label="Your email address"
                  type="email"
                  onChange={onChangeEmail}
                  onBlur={validateEmail}
                  value={email}
                  className={classnames(styles.input, 'is-full-width body-small', { [styles.error]: emailError })}
                  name="email"
                  id="k_id_email"
                />
              </div>
              {emailError && (
                <Typography className="mt-1" variant="body-small" display="block" color="danger">{emailError}</Typography>
              )}
            </div>
          </div>
          {storeLocation === 'NZ' && (
            <div className={styles.consentContainer}>
              <label>
                <CheckBox name="marketing" checked={smsConsent.includes('marketing')} onChange={changeSmsConsent} />
                <Typography className={styles.consentText}>
                  I consent to receiving marketing text messages (e.g. cart reminders and back-in-stock notifications) at the number provided.
                </Typography>
              </label>
              <label>
                <CheckBox name="transactional" checked={smsConsent.includes('transactional')} onChange={changeSmsConsent} />
                <Typography className={styles.consentText}>
                  I consent to receiving non-marketing updates and information, such as shipping and account updates at the number provided.
                </Typography>
              </label>
              {smsConsentError && (
                <Typography className="mt-1" variant="body-small" display="block" color="danger">{smsConsentError}</Typography>
              )}
            </div>
          )}
          <Button
            renderAs="button"
            className={classnames('mt-3 button primary ck_subscribe')}
            size={isMobile ? 'medium' : 'large'}
            loading={isFetching}
            fullwidth="mobile"
            type="submit"
          >
            Sign Up
          </Button>
        </form>
        <TermsAndPrivacyLinks />
      </div>
    </div>
  );
};

FooterSignUpForm.propTypes = {
  nlText: PropTypes.string,
  onSignUpSuccess: PropTypes.func.isRequired,
};

FooterSignUpForm.defaultProps = {
  nlText: null,
};

export { FooterSignUpForm };
